import { Text } from '@chatsdet/ui';
import { Info, Warning, Play } from '@phosphor-icons/react';
import type { NoVideoReason } from './useTestRunDetail';

const REASON_COPY: Record<NoVideoReason, { title: string; body: string; tone: 'info' | 'warning' }> = {
  no_browser_interaction: {
    title: 'Nothing to record',
    body: 'This run completed without driving the browser, so no video was captured.',
    tone: 'info',
  },
  api_only_workflow: {
    title: 'No browser steps',
    body: 'This workflow only runs API checks — there is no UI to record.',
    tone: 'info',
  },
  run_failed_before_recording: {
    title: 'Recording never started',
    body: 'The run failed before the browser session could record (e.g. a login or setup error stopped it early).',
    tone: 'warning',
  },
  session_end_failed: {
    title: 'Recording was lost',
    body: 'The browser session ended unexpectedly before the video could be saved.',
    tone: 'warning',
  },
  recording_failed: {
    title: 'Recording failed',
    body: 'The browser ran but no video could be saved for this run. The rest of the run results are still available.',
    tone: 'warning',
  },
};

type Props = {
  reason: NoVideoReason | null;
  isRunning: boolean;
};

export function NoVideoEmptyState({ reason, isRunning }: Props) {
  if (isRunning) {
    return (
      <div className="aspect-video flex items-center justify-center border-2 border-dashed border-border rounded-lg bg-muted/30">
        <div className="text-center">
          <Play size={48} className="mx-auto text-muted-foreground mb-2" />
          <Text className="text-muted-foreground">Recording in progress…</Text>
        </div>
      </div>
    );
  }

  const copy = reason ? REASON_COPY[reason] : null;

  if (!copy) {
    return (
      <div className="aspect-video flex items-center justify-center border-2 border-dashed border-border rounded-lg bg-muted/30">
        <div className="text-center">
          <Play size={48} className="mx-auto text-muted-foreground mb-2" />
          <Text className="text-muted-foreground">No recording available</Text>
        </div>
      </div>
    );
  }

  const Icon = copy.tone === 'warning' ? Warning : Info;
  const toneClasses =
    copy.tone === 'warning'
      ? 'bg-warning/10 border-warning/20 text-warning'
      : 'bg-muted/50 border-border text-muted-foreground';

  return (
    <div
      className={`aspect-video flex items-center justify-center border-2 border-dashed rounded-lg ${toneClasses}`}
    >
      <div className="text-center max-w-sm px-6">
        <Icon size={40} weight="fill" className="mx-auto mb-3" />
        <Text size="lg" weight="semibold" className="block mb-1">
          {copy.title}
        </Text>
        <Text size="sm" className="opacity-80">
          {copy.body}
        </Text>
      </div>
    </div>
  );
}
